import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const ServicesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${SITE_URL}/services#webpage`,
      url: `${SITE_URL}/services`,
      name: '岡山市のリフォーム・工事サービス一覧｜外装・内装・メンテナンス｜株式会社おかやま工務店',
      description:
        '岡山市の株式会社おかやま工務店が提供する外装工事・内装工事・リフォーム・メンテナンスのサービス詳細。料金目安・工事期間も掲載。見積もり無料。',
      inLanguage: 'ja',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      dateModified: new Date().toISOString().split('T')[0],
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['h1', 'h2'],
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'ホーム', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'サービス内容', item: `${SITE_URL}/services` },
        ],
      },
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@type': 'Service',
              name: '外装工事',
              url: `${SITE_URL}/services#外装工事`,
              provider: { '@id': `${SITE_URL}/#business` },
              areaServed: { '@type': 'City', name: '岡山市' },
              description: '外壁の修理・塗装から屋根の補修まで、住まいの外観を美しく保ちます。',
            },
          },
          {
            '@type': 'ListItem',
            position: 2,
            item: {
              '@type': 'Service',
              name: '内装工事',
              url: `${SITE_URL}/services#内装工事`,
              provider: { '@id': `${SITE_URL}/#business` },
              areaServed: { '@type': 'City', name: '岡山市' },
              description: '床の張り替え、壁紙の交換、建具の修理など、室内の快適性を高める工事。',
            },
          },
          {
            '@type': 'ListItem',
            position: 3,
            item: {
              '@type': 'Service',
              name: 'リフォーム',
              url: `${SITE_URL}/services#リフォーム`,
              provider: { '@id': `${SITE_URL}/#business` },
              areaServed: { '@type': 'City', name: '岡山市' },
              description: 'キッチン・浴室・トイレなどの水回りから和室を洋室に変更するリフォームまで。',
            },
          },
          {
            '@type': 'ListItem',
            position: 4,
            item: {
              '@type': 'Service',
              name: 'メンテナンス',
              url: `${SITE_URL}/services#メンテナンス`,
              provider: { '@id': `${SITE_URL}/#business` },
              areaServed: { '@type': 'City', name: '岡山市' },
              description: '定期的な点検で住まいの問題を早期発見。雨漏りやシロアリ被害にも対応。',
            },
          },
        ],
      },
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-services';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-services');
      if (el) el.remove();
    };
  }, []);

  const services = [
    {
      name: '外装工事',
      description: '外壁の修理・塗装から屋根の補修まで、住まいの外観を美しく保ちます。経年劣化による傷みや、台風などの自然災害による損傷にも迅速に対応。防水性能の向上や断熱効果の改善も行います。',
      details: ['外壁修理・塗装', '屋根修理・葺き替え', '雨樋の修理・交換', '防水工事'],
      price: '50,000円〜',
      period: '3日〜2週間',
      image: 'https://public.readdy.ai/ai/img_res/bf533dea5cd2766b5354956413104be0.jpg',
    },
    {
      name: '内装工事',
      description: '床の張り替え、壁紙の交換、建具の修理など、室内の快適性を高める工事を行います。ライフスタイルの変化に合わせた間取り変更や、バリアフリー化にも対応。お客様のご要望に合わせた最適なプランをご提案します。',
      details: ['床材の張り替え', '壁紙・クロスの交換', '建具の修理・交換', '間仕切り工事'],
      price: '30,000円〜',
      period: '2日〜1週間',
      image: 'https://readdy.ai/api/search-image?query=skilled%20Japanese%20carpenter%20installing%20beautiful%20wooden%20flooring%20in%20modern%20Japanese%20home%20interior%20using%20precision%20tools%20wearing%20professional%20work%20clothes%20natural%20indoor%20lighting%20clean%20simple%20background%20showing%20quality%20wood%20materials%20detailed%20interior%20construction%20craftsmanship&width=800&height=800&seq=service-detail2&orientation=squarish',
    },
    {
      name: 'リフォーム',
      description: 'キッチン、浴室、トイレなどの水回りから、和室を洋室に変更するリフォームまで幅広く対応。古くなった設備を最新のものに交換し、快適で使いやすい空間を実現します。省エネ性能の向上も考慮した提案を行います。',
      details: ['キッチンリフォーム', '浴室・トイレリフォーム', '和室から洋室への変更', '増改築工事'],
      price: '100,000円〜',
      period: '1週間〜1ヶ月',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20renovation%20specialist%20consulting%20with%20blueprints%20and%20measuring%20tools%20in%20modern%20kitchen%20space%20professional%20planning%20scene%20wearing%20work%20attire%20natural%20indoor%20lighting%20clean%20simple%20background%20showing%20kitchen%20remodel%20area%20quality%20consultation%20service&width=800&height=800&seq=service-detail3&orientation=squarish',
    },
    {
      name: 'メンテナンス',
      description: '定期的な点検で住まいの問題を早期発見。雨漏りやシロアリ被害など、放置すると大きな被害につながる問題も、早期対応で最小限の費用で解決できます。長く安心して住み続けられる住まいづくりをサポートします。',
      details: ['定期点検サービス', '雨漏り修理', 'シロアリ対策', '緊急修理対応'],
      price: '10,000円〜',
      period: '即日〜3日',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20maintenance%20professional%20carefully%20inspecting%20roof%20structure%20and%20checking%20for%20damage%20with%20inspection%20tools%20wearing%20safety%20gear%20natural%20daylight%20clean%20simple%20background%20showing%20roof%20maintenance%20work%20preventive%20care%20detailed%20examination&width=800&height=800&seq=service-detail4&orientation=squarish',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center mt-20">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=experienced%20Japanese%20master%20carpenter%20using%20professional%20hand%20tools%20working%20with%20quality%20wood%20materials%20in%20workshop%20detailed%20craftsmanship%20close%20up%20of%20skilled%20hands%20natural%20lighting%20warm%20wood%20tones%20clean%20simple%20background%20traditional%20Japanese%20carpentry%20professional%20work%20scene&width=1920&height=800&seq=services-hero&orientation=landscape"
            alt="サービス内容"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 w-full">
          <div className="max-w-2xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-6" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              サービス内容
            </h1>
            <p className="text-lg sm:text-xl text-white leading-relaxed" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              外装から内装まで、幅広く対応
            </p>
          </div>
        </div>
      </section>

      {/* Services Detail Section */}
      <section className="py-16 lg:py-24 px-6 bg-white">
        <div className="max-w-[1200px] mx-auto">
          <div className="space-y-16 lg:space-y-24">
            {services.map((service, index) => (
              <article key={index} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 lg:gap-12 items-center`}>
                {/* Image */}
                <div className="w-full lg:w-1/2">
                  <div className="aspect-square rounded-2xl overflow-hidden shadow-xl">
                    <img
                      src={service.image}
                      alt={service.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="w-full lg:w-1/2">
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#1a1a2e] mb-6" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    {service.name}
                  </h2>
                  <p className="text-base lg:text-lg text-gray-700 leading-relaxed mb-6">
                    {service.description}
                  </p>

                  <div className="space-y-4 mb-6">
                    <h3 className="text-lg font-bold text-[#1a1a2e] mb-3">対応内容</h3>
                    <ul className="space-y-2">
                      {service.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start">
                          <i className="ri-check-line text-[#ff6b35] text-xl mr-2 mt-1 flex-shrink-0"></i>
                          <span className="text-gray-700">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="grid grid-cols-2 gap-4 p-6 bg-[#faf8f3] rounded-xl">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">料金目安</p>
                      <p className="text-xl font-bold text-[#1a1a2e]">{service.price}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">工事期間</p>
                      <p className="text-xl font-bold text-[#1a1a2e]">{service.period}</p>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 px-6 bg-[#faf8f3]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#1a1a2e] mb-6" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
            お見積もりは無料です
          </h2>
          <p className="text-lg text-gray-700 mb-10 leading-relaxed">
            どんな小さな工事でもお気軽にご相談ください。<br />
            経験豊富なスタッフが丁寧に対応いたします。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:086-012-3456"
              className="inline-flex items-center justify-center px-8 py-4 bg-[#ff6b35] text-white text-base lg:text-lg font-bold rounded-full hover:bg-[#ff8555] transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill mr-2"></i>
              086-012-3456
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-[#1a1a2e] text-white text-base lg:text-lg font-bold rounded-full hover:bg-[#2a2a3e] transition-all duration-300 hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-mail-line mr-2"></i>
              お問い合わせフォーム
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;